import { VendorUserType } from "auth/VendorAuth/vendorUserSlice";
import FileUpload from "components/FileUpload/FileUpload";
import RfiReadOnlyAttachments from "components/RfiReadOnlyAttachments";
import { useAppSelector } from "hooks/redux";
import { Link } from "react-router-dom";
import { selectRfi } from "store/rfiSlice";
import { selectVendorResponse } from "store/vendorResponseSlice";
import RfiElement from "types/RfiElement";
import {
  Attachment,
  AttachmentType,
  ElementAnswer,
  SectionGroup,
  fieldTypes,
  sectionNames,
} from "types/RfiTypes";
import VendorEditAttachments from "./VendorEditAttachments";
import { vendorNavPages } from "./VendorNav";

interface IRfiVendorAllProps {
  user: VendorUserType;
  setSelectedPage?: (page: number) => void;
  readOnly?: boolean;
}

const VendorRfiAttachments = (props: IRfiVendorAllProps) => {
  // Hooks
  const rfi = useAppSelector(selectRfi);
  const vendorResponse = useAppSelector(selectVendorResponse);

  // Render
  return (
    <div className="rfi-content">
      {/* General Vendor Response Attachments */}
      <div>
        <h1>General Attachments</h1>
        <div className="guidance-text">
          Upload one or many attachments here. Once uploaded, enter a title if
          needed. This list of attachments should not include any that were
          uploaded in answer to a question on the questionnaire.
        </div>
        {!(props?.readOnly ?? false) ? (
          <>
            <FileUpload
              rfiId={rfi.rfi?.rfiId ?? 0}
              attachmentType={AttachmentType.VENDOR_RESPONSE}
              vendorResponseId={vendorResponse.vendorResponse?.vendorResponseId}
            />
            <div style={{ marginTop: "30px", marginLeft: "15px" }}>
              <VendorEditAttachments
                attachments={vendorResponse.attachments ?? ([] as Attachment[])}
                key="editor1"
              ></VendorEditAttachments>
            </div>
          </>
        ) : (
          <RfiReadOnlyAttachments
            attachments={vendorResponse.attachments ?? ([] as Attachment[])}
          />
        )}
      </div>

      {/* Questionnaire Attachments Review */}
      <div className="my-2">
        <h1>Questionnaire Attachments</h1>
        <div className="guidance-text">
          This is a list of attachments that were uploaded as answers to
          questions on the questionnaire, displayed here as a reference only.
        </div>
        <div className="my-2">
          {rfi.vendorInfo?.map((element: RfiElement, elementIndex: number) => {
            if (element.fieldType === fieldTypes.ATTACHMENT) {
              let sectionGroup = element.sections?.find(
                (section: SectionGroup) =>
                  section.sectionGroupName === sectionNames.VENDOR_INFO
              );
              return (
                <div key={`attach-vendorinfo-element-${elementIndex}`}>
                  <label>{`${sectionGroup?.questionNumber}. ${element.fieldLabel} `}</label>
                  <ul key={`attachmentelement-${elementIndex}`}>
                    {element.answers?.map(
                      (answer: ElementAnswer, answerIndex: number) => {
                        if (answer.attachment) {
                          return (
                            <li key={answer.attachment.id}>
                              {answer.attachment.title
                                ? `${answer.attachment.title} (`
                                : ""}
                              <Link
                                to={`/api/v1/rfi/${answer.attachment.rfiId}/Attachments/${answer.attachment.attachmentLink}`}
                                target="_blank"
                                download
                              >
                                {answer.attachment.filename}
                              </Link>
                              {answer.attachment.title ? `)` : ""}
                            </li>
                          );
                        }
                      }
                    )}
                  </ul>
                </div>
              );
            }
          })}
          {rfi.questionnaire?.map(
            (element: RfiElement, elementIndex: number) => {
              if (element.fieldType === fieldTypes.ATTACHMENT) {
                let sectionGroup = element.sections?.find(
                  (section: SectionGroup) =>
                    section.sectionGroupName === sectionNames.QUESTIONNAIRE
                );
                return (
                  <div key={`attach-questionnaire-element-${elementIndex}`}>
                    <label>{`${sectionGroup?.questionNumber}. ${element.fieldLabel} `}</label>
                    <ul key={`attachmentelement-${elementIndex}`}>
                      {element.answers?.map(
                        (answer: ElementAnswer, answerIndex: number) => {
                          if (answer.attachment) {
                            return (
                              <li key={answer.attachment.id}>
                                {answer.attachment.title
                                  ? `${answer.attachment.title} (`
                                  : ""}
                                <Link
                                  to={`/api/v1/rfi/${answer.attachment.rfiId}/Attachments/${answer.attachment.attachmentLink}`}
                                  target="_blank"
                                  download
                                >
                                  {answer.attachment.filename}
                                </Link>
                                {answer.attachment.title ? `)` : ""}
                              </li>
                            );
                          }
                        }
                      )}
                    </ul>
                  </div>
                );
              }
            }
          )}
        </div>
      </div>

      {/* Navigation Buttons */}
      {props.user.isAuthenticated &&
        (vendorResponse.vendorResponse?.vendorResponseId ?? 0) > 0 &&
        props.setSelectedPage && (
          <div className="mt-5">
            <button
              className="btn btn-primary m-1"
              role="button"
              onClick={() =>
                props.setSelectedPage &&
                props.setSelectedPage(vendorNavPages.QUESTIONNAIRE)
              }
            >
              <i className="fa-solid fa-chevron-left"></i> Step 2: Questionnaire
            </button>
            <button
              className="btn btn-primary m-1"
              role="button"
              onClick={() =>
                props.setSelectedPage &&
                props.setSelectedPage(vendorNavPages.VALIDATE)
              }
            >
              {!props.readOnly
                ? "Step 4: Validate and submit"
                : "Step 4: Submission Information"}
              <i className="fa-solid fa-chevron-right mx-1"></i>
            </button>
          </div>
        )}
    </div>
  );
};

export default VendorRfiAttachments;
